'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Phone, Mail } from 'lucide-react';

interface FooterLink {
  id: string;
  title: string;
  url: string;
  order: number;
  isActive: boolean;
}

interface FooterSection {
  id: string;
  title: string;
  order: number;
  isActive: boolean;
  links: FooterLink[];
}

interface FooterSettings {
  id: string;
  companyName: string;
  companyAddress: string;
  companyPhone?: string;
  companyEmail: string;
  companyNIP: string;
  companyREGON: string;
  backgroundColor: string;
  textColor: string;
  accentColor: string;
}

export default function Footer() {
  const [sections, setSections] = useState<FooterSection[]>([]);
  const [settings, setSettings] = useState<FooterSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const [sectionsRes, settingsRes] = await Promise.all([
          fetch('/api/footer/sections'),
          fetch('/api/footer/settings')
        ]);

        if (!sectionsRes.ok || !settingsRes.ok) {
          throw new Error('Błąd podczas pobierania danych stopki');
        }

        const [sectionsData, settingsData] = await Promise.all([
          sectionsRes.json(),
          settingsRes.json()
        ]);

        setSections(sectionsData);
        setSettings(settingsData);
      } catch (error) {
        console.error('Błąd podczas pobierania danych stopki:', error);
        setError('Nie udało się pobrać danych stopki');
      }
    };

    fetchFooterData();
  }, []);

  if (error) {
    console.error(error);
    return null;
  }

  if (!settings) return null;

  return (
    <footer style={{ backgroundColor: settings.backgroundColor, color: settings.textColor }}>
      <div className="container mx-auto py-12 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Sekcja z danymi firmy */}
          <div>
            <h3 className="text-lg font-bold mb-4" style={{ color: settings.accentColor }}>
              {settings.companyName}
            </h3>
            <div className="space-y-2">
              <p>{settings.companyAddress}</p>
              <p>NIP: {settings.companyNIP}</p>
              <p>REGON: {settings.companyREGON}</p>
              {settings.companyPhone && (
                <a 
                  href={`tel:${settings.companyPhone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-200"
                  style={{ color: settings.textColor }}
                >
                  <Phone className="h-4 w-4" />
                  {settings.companyPhone}
                </a>
              )}
              <a 
                href={`mailto:${settings.companyEmail}`}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-200"
                style={{ color: settings.textColor }}
              >
                <Mail className="h-4 w-4" />
                {settings.companyEmail}
              </a>
            </div>
          </div>

          {/* Dynamiczne sekcje */}
          {sections
            .filter(section => section.isActive)
            .sort((a, b) => a.order - b.order)
            .map((section) => (
              <div key={section.id}>
                <h3 className="text-lg font-bold mb-4" style={{ color: settings.accentColor }}>
                  {section.title}
                </h3>
                <ul className="space-y-2">
                  {section.links
                    .filter(link => link.isActive)
                    .sort((a, b) => a.order - b.order)
                    .map((link) => (
                      <li key={link.id}>
                        <Link 
                          href={link.url}
                          className="hover:opacity-80 transition-opacity duration-200"
                          style={{ color: settings.textColor }}
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </div>

        {/* Dolny pasek */}
        <div 
          className="mt-8 pt-8 text-sm text-center"
          style={{ borderTop: `1px solid ${settings.accentColor}` }}
        >
          © {new Date().getFullYear()} {settings.companyName}. Wszelkie prawa zastrzeżone.
        </div>
      </div>
    </footer>
  );
} 